import React, { useState } from 'react';
import api from '../services/api';

const Login = ({ onLogin, onNavigate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotTin, setForgotTin] = useState('');
  const [forgotMobile, setForgotMobile] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleSubmit = async e => {
    e && e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/login', { email, password });
      // expect { token, user } (adjust according to your backend)
      const { token, user } = res.data || {};
      if (token) {
        localStorage.setItem('token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      if (onLogin) onLogin(user || res.data);
    } catch (err) {
      console.error('Login failed', err);
      setError(err?.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e && e.preventDefault();
    setForgotMsg('');

    // basic client-side validation
    if (!forgotTin || !forgotMobile || !forgotEmail) {
      setForgotMsg('Provide TIN, mobile and email.');
      return;
    }
    if (!forgotNewPassword || !forgotConfirmPassword) {
      setForgotMsg('Provide new password and confirm it.');
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotMsg('New password and confirmation do not match.');
      return;
    }

    setForgotLoading(true);
    try {
      // backend expected payload: { tin, mobile, email, newPassword }
      // adjust if your backend uses different field names or endpoints
      const payload = {
        tin: forgotTin,
        mobile: forgotMobile,
        email: forgotEmail,
        newPassword: forgotNewPassword
      };
      const res = await api.post('/auth/forgot', payload);
      // assume backend returns success message on password reset
      setForgotMsg(res?.data?.message || 'Password updated. You may now sign in.');
      // clear sensitive fields
      setForgotNewPassword('');
      setForgotConfirmPassword('');
    } catch (err) {
      console.error('Forgot password failed', err);
      setForgotMsg(err?.response?.data?.message || 'Failed to reset password.');
    } finally {
      setForgotLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background:
        'linear-gradient(135deg,#f6f8ff 0%, #ffffff 40%, #f0f7ff 100%)',
      padding: 20,
      fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    },
    card: {
      width: '100%',
      maxWidth: 480,
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 8px 30px rgba(20, 30, 70, 0.08)',
      padding: '28px 28px',
      boxSizing: 'border-box',
      border: '1px solid rgba(16,24,40,0.04)'
    },
    header: { marginBottom: 18, textAlign: 'center' },
    logo: { width: 56, height: 56, borderRadius: 10, display: 'inline-block', background: 'linear-gradient(135deg,#0f172a,#06b6d4)', marginBottom: 12 },
    title: { margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' },
    subtitle: { margin: 0, marginTop: 6, fontSize: 13, color: '#6b7280' },
    form: { display: 'grid', gap: 12, marginTop: 12 },
    input: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid #e6eef8', outline: 'none', fontSize: 14, boxSizing: 'border-box' },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#6b7280' },
    button: { width: '100%', padding: '12px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', color: '#fff', fontWeight: 700, background: 'linear-gradient(90deg,#0f172a,#06b6d4)', boxShadow: '0 8px 20px rgba(6,182,212,0.08)' },
    smallLink: { color: '#2563eb', cursor: 'pointer', textDecoration: 'none' },
    error: { color: '#b91c1c', fontSize: 13, marginTop: 6 },
    footer: { marginTop: 14, fontSize: 13, color: '#94a3b8', textAlign: 'center' },
    eyeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 6 },
    inputWrapper: { position: 'relative' },
    eyeWrap: { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }
  };

  // manage focus style without external libs
  const [focused, setFocused] = useState(null);
  const focusStyle = key => (focused === key ? { boxShadow: '0 4px 14px rgba(99,102,241,0.08)', borderColor: '#6366f1' } : {});

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.logo} />
          <h1 style={styles.title}>Doc Sheba</h1>
          <p style={styles.subtitle}>Sign in to manage your profile, clients and documents</p>
        </div>

        <form style={styles.form} onSubmit={handleSubmit} noValidate>
          <div>
            <label style={{ fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Email</label>
            <input
              style={{ ...styles.input, ...focusStyle('email') }}
              value={email}
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              placeholder="you@example.com"
              type="email"
              required
              autoComplete="username"
            />
          </div>

          <div>
            <label style={{ fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Password</label>
            <div style={styles.inputWrapper}>
              <input
                style={{ ...styles.input, ...focusStyle('password'), paddingRight: 44 }}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocused('password')}
                onBlur={() => setFocused(null)}
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
              />
              <div style={styles.eyeWrap}>
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword(s => !s)}
                  style={styles.eyeBtn}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          </div>

          <div style={styles.row}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" />
              <span style={{ color: '#475569' }}>Remember me</span>
            </label>
            <button type="button" onClick={() => setForgotOpen(true)} style={{ ...styles.smallLink, background: 'none', border: 'none' }}>Forgot?</button>
          </div>

          <div>
            <button style={styles.button} type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </div>

          {error && <div style={styles.error}>{error}</div>}
        </form>

        <div style={styles.footer}>
          <div>Don't have an account? <button onClick={() => onNavigate && onNavigate('register')} style={{ ...styles.smallLink, background: 'none', border: 'none', padding: 0 }}>Register</button></div>
        </div>
      </div>

      {/* Forgot password modal */}
      {forgotOpen && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <form onSubmit={handleForgot} style={{ background: '#fff', padding: 20, borderRadius: 8, width: 460, maxWidth: '95vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Forgot / Reset password</h3>
              <button type="button" onClick={() => setForgotOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <label>TIN Number</label>
              <input value={forgotTin} onChange={e => setForgotTin(e.target.value)} type="text" required />

              <label>Mobile number</label>
              <input value={forgotMobile} onChange={e => setForgotMobile(e.target.value)} type="tel" required />

              <label style={{ marginTop: 6 }}>Email</label>
              <input value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} type="email" required />

              <label style={{ marginTop: 6 }}>New password</label>
              <input value={forgotNewPassword} onChange={e => setForgotNewPassword(e.target.value)} type="password" required />

              <label>Confirm new password</label>
              <input value={forgotConfirmPassword} onChange={e => setForgotConfirmPassword(e.target.value)} type="password" required />

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={() => setForgotOpen(false)} style={{ padding: '8px 12px' }}>Cancel</button>
                <button type="submit" disabled={forgotLoading} style={{ padding: '8px 12px', background: '#0b69ff', color: '#fff', border: 'none' }}>
                  {forgotLoading ? 'Processing...' : 'Reset password'}
                </button>
              </div>
              {forgotMsg && <div style={{ marginTop: 8, color: forgotMsg.toLowerCase().includes('failed') ? '#b91c1c' : '#0b3' }}>{forgotMsg}</div>}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Login;